import { useReducer, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from '@wfp-common/hooks/useNavigate';

import { useProductGroupsOptions } from '../../utils/hooks/select-options/use-product-groups-options.hook';
import { loadPrices } from '../utils/load-prices.utils';
import { compose } from '../../utils/utils';
import { errorAction } from '../../utils/notifications';
import State from '../../app/store/state';
import { Outlets } from '../types/outlet.type';
import { getAllOutlets, getVendorsForOutlet } from '../../apiClient';
import { createProductPriceVersionUpdateRequest } from '../../api/product-management';

interface AvailableOutlet {
    outlet: string;
    vendorCount: number;
}

interface VendorOption {
    branchCodeId: number;
    outlet: string;
    vendorId: string;
    vendorNickname: string;
}

export function useEditOutlets(groupName: string, passedGroup?: { value: string; label: string }) {
    const [current, send] = useReducer(reducer, machine.initial);
    const [productGroupOptions] = useProductGroupsOptions();
    const [group, setGroup] = useState<{ value: string; label: string } | null>(passedGroup || null);
    const [originalOutlets, setOriginalOutlets] = useState<Outlets>({});
    const [outlets, setOutlets] = useState<Outlets>({});
    const [isParking, setIsParking] = useState(false);
    const [isAddOutletsModalOpen, setIsAddOutletsModalOpen] = useState(false);
    const [availableOutlets, setAvailableOutlets] = useState<AvailableOutlet[]>([]);
    const [isAddVendorsModalOpen, setIsAddVendorsModalOpen] = useState<string | false>(false);
    const [availableVendors, setAvailableVendors] = useState<VendorOption[]>([]);
    const dispatch = useDispatch();
    const timezone = useSelector((state: State) => state.appConfig.timeZone);
    const navigate = useNavigate();

    useEffect(() => {
        // If group was passed through navigation state, use it
        if (passedGroup) {
            setGroup(passedGroup);
            return;
        }
        
        // Otherwise, try to find it in productGroupOptions
        if (Array.isArray(productGroupOptions) && groupName) {
            const decodedGroupName = decodeURIComponent(groupName);
            const foundGroup = productGroupOptions.find(
                option => option.label.toLowerCase() === decodedGroupName.toLowerCase()
            );
            
            if (foundGroup) {
                setGroup(foundGroup);
            } else {
                // If group not found, redirect back to products page
                navigate('/products');
            }
        }
    }, [productGroupOptions, groupName, navigate, passedGroup]);

    useEffect(() => {
        if (group) {
            loadOutlets(group.value);
        }
    }, [group]);

    const loadOutlets = async (groupId: string) => {
        try {
            send('LOAD');
            const prices = await loadPrices(groupId, timezone);
            const outletsData = prices.outlets || {};
            setOriginalOutlets(outletsData);
            setOutlets(outletsData);
        } catch (error) {
            compose(dispatch, errorAction)(`Unable to load outlets for group ${group?.label}`);
            navigate('/products');
        } finally {
            send('DONE');
        }
    };

    const handleRemoveVendor = (outletName: string, vendorId: string) => {
        setOutlets((prev) => {
            const updated = { ...prev };
            const list = (updated[outletName] || []).filter((v) => v.vendorId !== vendorId);
            if (list.length === 0) {
                // keep empty outlet entry as empty array to reflect that outlet exists but without vendors
                updated[outletName] = [];
            } else {
                updated[outletName] = list;
            }
            return updated;
        });
    };

    const handleParkChanges = async () => {
        if (!hasChanges || !group) return;

        try {
            setIsParking(true);

            const allBranchCodeIds = Object.values(outlets || {})
                .flat()
                .map((v) => v.branchCodeId);
            const uniqueBranchCodeIds = Array.from(new Set(allBranchCodeIds));
            await createProductPriceVersionUpdateRequest(Number(group.value), uniqueBranchCodeIds);

            navigate('/products', { flashSuccess: 'Outlet changes parked successfully' });
        } catch (error) {
            if (error.status === 409) {
                compose(dispatch, errorAction)('Failed to park outlet changes, there is already a pending update request');
            } else {
                compose(dispatch, errorAction)('Failed to park outlet changes');
            }
        } finally {
            setIsParking(false);
        }
    };

    const handleAddOutlets = async () => {
        if (!group) return;
        
        try {
            setIsAddOutletsModalOpen(true);
            const allOutlets = await getAllOutlets();
            
            const currentGroupOutletNames = Object.keys(outlets);
            const availableOutlets = allOutlets.filter(
                (outlet: AvailableOutlet) => !currentGroupOutletNames.includes(outlet.outlet)
            );
            
            setAvailableOutlets(availableOutlets);
        } catch (error) {
            compose(dispatch, errorAction)('Failed to load available outlets');
            setIsAddOutletsModalOpen(false);
        }
    };

    const handleCloseAddOutletsModal = () => {
        setIsAddOutletsModalOpen(false);
        setAvailableOutlets([]);
    };

    const handleSelectOutlet = async (selectedOutlet: AvailableOutlet) => {
        if (!group) return;
        try {
            const newOutlets = { ...outlets };
            if (!newOutlets[selectedOutlet.outlet]) {
                newOutlets[selectedOutlet.outlet] = [];
            }
            setOutlets(newOutlets);
            setAvailableOutlets((prev) => prev.filter((o) => o.outlet !== selectedOutlet.outlet));
            compose(dispatch, (message) => ({ type: 'NOTIFICATION_SUCCESS', payload: message }))(
                `Added outlet "${selectedOutlet.outlet}". No vendors yet.`
            );
        } catch (error) {
            compose(dispatch, errorAction)('Failed to add outlet');
        }
    };

    const handleAddVendors = async (outletName: string) => {
        try {
            setIsAddVendorsModalOpen(outletName);
            const vendorOutlets = await getVendorsForOutlet(outletName);
            const existingVendorIds = new Set((outlets[outletName] || []).map((v) => v.vendorId));
            const options: VendorOption[] = vendorOutlets
                .filter((v) => !existingVendorIds.has(v.vendorId))
                .map((v) => ({
                    branchCodeId: v.branchCodeId,
                    outlet: v.outlet,
                    vendorId: v.vendorId,
                    vendorNickname: v.vendorNickname,
                }));
            setAvailableVendors(options);
        } catch (e) {
            compose(dispatch, errorAction)('Failed to load vendors for outlet');
            setIsAddVendorsModalOpen(false);
        }
    };

    const handleCloseAddVendorsModal = () => {
        setIsAddVendorsModalOpen(false);
        setAvailableVendors([]);
    };

    const handleConfirmVendors = (vendorsToAdd: VendorOption[]) => {
        const outletName = isAddVendorsModalOpen as string;
        if (!outletName || !group) return;
        const mapped = vendorsToAdd.map((vendorOutlet) => ({
            productGroupId: Number(group.value),
            branchCodeId: vendorOutlet.branchCodeId,
            outlet: vendorOutlet.outlet,
            vendorId: vendorOutlet.vendorId,
            vendorNickname: vendorOutlet.vendorNickname,
        }));
        setOutlets((prev) => ({
            ...prev,
            [outletName]: [...(prev[outletName] || []), ...mapped],
        }));
        setIsAddVendorsModalOpen(false);
        setAvailableVendors([]);
        compose(dispatch, (message) => ({ type: 'NOTIFICATION_SUCCESS', payload: message }))(
            `Added ${mapped.length} vendor(s) to ${outletName}`
        );
    };

    const hasChanges = JSON.stringify(originalOutlets) !== JSON.stringify(outlets);
    const isLoading = current === 'loading';

    return {
        isLoading,
        outlets,
        originalOutlets,
        group,
        handleRemoveVendor,
        handleParkChanges,
        hasChanges,
        isParking,
        handleAddOutlets,
        handleAddVendors,
        isAddOutletsModalOpen,
        isAddVendorsModalOpen,
        availableOutlets,
        availableVendors,
        handleCloseAddOutletsModal,
        handleCloseAddVendorsModal,
        handleSelectOutlet,
        handleConfirmVendors,
    };
}

const machine = {
    initial: 'idle',
    states: {
        idle: {
            on: { LOAD: 'loading' },
        },
        loading: {
            on: { DONE: 'loaded' },
        },
        loaded: {
            LOAD: 'loading',
        },
    },
};

function reducer(state, event) {
    return machine.states?.[state]?.on?.[event] || state;
}